import { CommonPaymentMethods } from '@ctct/infrastructure-layer';
import type { PaymentButtonPageStore } from '@ctct/state-client-layer';
import type { PayForPlanMutation } from '@ctct/state-server-layer';

import { PayNowPermission, type TermsStrategy } from './TermsStrategy';
import type { TEventSource } from '../../../helpers';
import type { TranslationGetter } from '../../../types';
import { paymentButtonEventBus } from '../PaymentButtonEventBus';

export class PaymentButtonService {
  constructor(
    private pageStore: PaymentButtonPageStore,
    private payForPlanMutation: PayForPlanMutation,
    private t: TranslationGetter,
    private getTermsStrategy: (isPadPayment: boolean) => TermsStrategy,
    private isMobile: boolean,
    private isSingleBillChildAccount: boolean
  ) {
    paymentButtonEventBus.emit('payForPlanLoading', payForPlanMutation.isPending, {
      source: 'asyncResponse',
    });

    if (payForPlanMutation.isError) {
      paymentButtonEventBus.emit('payForPlanError', payForPlanMutation.error.message, {
        source: 'asyncResponse',
      });
    }

    if (payForPlanMutation.isSuccess) {
      paymentButtonEventBus.emit('payForPlanSuccess', true, {
        source: 'asyncResponse',
      });
    }
  }

  public api() {
    return {
      disable: () => {
        this.pageStore.setState({ disabled: true });
      },
      enable: () => {
        this.pageStore.setState({ disabled: false });
      },
      setPriceLabel: (priceLabel: string) => {
        this.pageStore.setState({ priceLabel });
      },
      onApplePayMobileAutoCheckout: () => {
        if (this.isMobile) {
          this.onPayForPlan('api');
        }
      },
      completeCheckout: () => {
        this.onPayForPlan('api');
      },
      setStripePaymentMethod: (paymentMethod: string) => {
        this.pageStore.setState({
          paymentButtonIsPadPaymentMethod: paymentMethod === CommonPaymentMethods.PreAuthorizedDebit,
        });
      },
      setBillingCountryCode: (countryCode: string | null) => {
        this.pageStore.setState({
          paymentButtonBillingCountryCode: countryCode,
        });
      },
    };
  }

  public data() {
    return {
      getButtonLabel: (priceLabel: string) => {
        return this.t('application-layer.payment-button.button-label', { priceLabel });
      },
      isSingleBillChildAccount: () => this.isSingleBillChildAccount,
    };
  }

  public handlers() {
    return {
      onPayButtonClick: () => {
        this.onPayForPlan('user');
      },
      onShowEmailPricingTiersClick: () => {
        paymentButtonEventBus.emit('showEmailPricingTiersClick', { source: 'user' });
      },
    };
  }

  private onPayForPlan(source: TEventSource) {
    if (this.isSingleBillChildAccount) {
      return;
    }

    const { paymentButtonHasAcceptedTerms } = this.pageStore.getState();
    const payNowPermission = this.getTermsStrategy(
      this.pageStore.getState().paymentButtonIsPadPaymentMethod
    ).checkWhetherPayNowAllowed({
      paymentButtonHasAcceptedTerms,
    });

    if (payNowPermission.status === PayNowPermission.Disallowed) {
      this.pageStore.setState({ paymentButtonTermsErrorMessage: this.t(payNowPermission.errorMessageI18nKey) });

      return;
    }

    paymentButtonEventBus.emit('payForPlanClicked', this.payForPlanMutation.mutateAsync, { source });
  }
}
